import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { t } from 'i18next';
import { MathUtils, Vector2 } from 'three';
import { Rectangle } from '@thive/fddm-types';

import { Accordion } from '@/modules/common/components/Accordion';
import { Dimensions } from '@/modules/common/components/Dimensions';
import { clampAngle180 } from '@/modules/common/helpers/math';
import { NumUtils } from '@/modules/common/utils';
import { offsetSegments } from '@/modules/floorplanDesign/helpers/helpers';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { Panel_Display_Mode } from '@/modules/floorplanDesign/types';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

type FloorplanDesignState = ReturnType<typeof useFloorplanDesignStore.getState>;

const getSelectedHighwayBounds = (
  selectedIds: string[],
  floorplanState: FloorplanDesignState,
): Rectangle | null => {
  const selectedItems = floorplanState.getAreasByIds(selectedIds).highwayAreas.map((area) => area.segments)

  if (selectedItems.length === 0) return null;
  if (selectedItems.length > 1) return null;
  if (selectedItems[0].length !== 1) return null;

  return RectangleUtils.fromSegment(selectedItems[0][0]);
};

const useSelectedHighwayDimension = (
  selectedIds: string[],
  value: keyof Rectangle,
): number | null =>
  useFloorplanDesignStore((state) => {
    const bounds = getSelectedHighwayBounds(selectedIds, state);
    return bounds ? bounds[value] : null;
  });

const useHighwayDimensions = () => {
  const panelModeState = useAppStore((state) => state.panelModeState);
  const selectedIds = useAppStore((state) => state.selectedIds);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const centerX = useSelectedHighwayDimension(selectedIds, 'centerX');
  const centerY = useSelectedHighwayDimension(selectedIds, 'centerY');
  const length = useSelectedHighwayDimension(selectedIds, 'length');
  const width = useSelectedHighwayDimension(selectedIds, 'width');
  const angle = useSelectedHighwayDimension(selectedIds, 'angle');

  const dimensions = useMemo(() => {
    if (
      centerX === null ||
      centerY === null ||
      length === null ||
      width === null ||
      angle === null
    ) {
      return null;
    }

    return { centerX, centerY, length, width, angle: MathUtils.radToDeg(angle) };
  }, [length, angle, width, centerX, centerY]);

  const currentDimensionsRef = useRef<Rectangle | null>(null);

  useEffect(() => {
    currentDimensionsRef.current = dimensions;
  }, [dimensions]);

  const getHighwaySelection = useCallback(() => {
    const floorplanState = useFloorplanDesignStore.getState();
    return floorplanState.getAreasByIds(selectedIds).highwayAreas;
  }, [selectedIds]);

  const onXChange = useCallback(
    (newValue: number) => {
      if (!currentDimensionsRef.current) return;

      const deltaX =
        NumUtils.mmToMetersRoundedToMm(newValue) - currentDimensionsRef.current.centerX;
      if (deltaX === 0) return;

      const selectedAreas = getHighwaySelection();
      selectedAreas.forEach((area) => {
        fddmSvc.updateArea({
          ...area,
          segments: offsetSegments(area.segments, new Vector2(deltaX, 0)),
        });
      });
    },
    [fddmSvc, getHighwaySelection],
  );

  const onYChange = useCallback(
    (newValue: number) => {
      if (!currentDimensionsRef.current) return;

      const deltaY =
        NumUtils.mmToMetersRoundedToMm(newValue) - currentDimensionsRef.current.centerY;
      if (deltaY === 0) return;

      getHighwaySelection().forEach((area) => {
        fddmSvc.updateArea({
          ...area,
          segments: offsetSegments(area.segments, new Vector2(0, deltaY)),
        });
      });
    },
    [fddmSvc, getHighwaySelection],
  );

  const onLengthChange = useCallback(
    (newValue: number) => {
      const newLength = NumUtils.mmToMetersRoundedToMm(newValue);
      if (newLength <= 0) return;

      const selectedHighway = getHighwaySelection()[0];

      const segment = selectedHighway?.segments[0];
      if (!selectedHighway || !segment) return;

      const rectangle = RectangleUtils.fromSegment(segment);
      const updatedSegment = RectangleUtils.toPartialSegment({
        ...rectangle,
        length: newLength,
      });

      fddmSvc.updateArea({
        ...selectedHighway,
        segments: minItemsArrayUtils.mapAtLeast1(selectedHighway.segments, (item, index) =>
          index === 0 ? { ...item, ...updatedSegment } : item,
        ),
      });
    },
    [fddmSvc, getHighwaySelection],
  );

  const onWidthChange = useCallback(
    (newValue: number) => {
      const newThickness = NumUtils.mmToMetersRoundedToMm(newValue);
      if (newThickness <= 0) return;

      const selectedHighway = getHighwaySelection()[0];

      const segment = selectedHighway?.segments[0];
      if (!selectedHighway || !segment) return;

      const rectangle = RectangleUtils.fromSegment(segment);
      const updatedSegment = RectangleUtils.toPartialSegment({
        ...rectangle,
        width: newThickness,
      });

      fddmSvc.updateArea({
        ...selectedHighway,
        segments: minItemsArrayUtils.mapAtLeast1(selectedHighway.segments, (item, index) =>
          index === 0 ? { ...item, ...updatedSegment } : item,
        ),
      });
    },
    [fddmSvc, getHighwaySelection],
  );

  const onAngleChange = useCallback(
    (newValue: number) => {
      // Segment bounding boxes use an inverted UI angle sign compared to segment geometry.
      const newAngle = MathUtils.degToRad(clampAngle180(newValue));

      const selectedHighway = getHighwaySelection()[0];

      const segment = selectedHighway?.segments[0];
      if (!selectedHighway || !segment) return;

      const rectangle = RectangleUtils.fromSegment(segment);
      const updatedSegment = RectangleUtils.toPartialSegment({
        ...rectangle,
        angle: newAngle,
      });

      fddmSvc.updateArea({
        ...selectedHighway,
        segments: minItemsArrayUtils.mapAtLeast1(selectedHighway.segments, (item, index) =>
          index === 0 ? { ...item, ...updatedSegment } : item,
        ),
      });
    },
    [fddmSvc, getHighwaySelection],
  );

  return {
    dimensions,
    onXChange,
    onYChange,
    onLengthChange,
    onWidthChange,
    onAngleChange,
    panelModeState,
    selectedIds,
  };
};

function HighwayDimensionsComponent() {
  const {
    dimensions,
    onXChange,
    onYChange,
    onLengthChange,
    onWidthChange,
    onAngleChange,
    panelModeState,
  } = useHighwayDimensions();

  const appMode = useAppStore((state) => state.mode);

  const isReadOnly = appMode !== 'EDITABLE';

  const templateModeDisabled =
    panelModeState === Panel_Display_Mode.TEMPLATE ||
    panelModeState === Panel_Display_Mode.TEMPLATE_LASSIE ||
    panelModeState === Panel_Display_Mode.TEMPLATE_LASSIE_MINI ||
    panelModeState === Panel_Display_Mode.TEMPLATE_SHUTTLE_RACK ||
    panelModeState === Panel_Display_Mode.TEMPLATE_HIK;

  const disabled = isReadOnly || templateModeDisabled;

  if (!dimensions) return null;

  return (
    <Accordion
      title={t<string>('interface:properties.dimensions.label', 'Dimensions')}
      unmountOnExit
      defaultExpanded
      sx={{ px: 2 }}
    >
      <Dimensions
        disabled={disabled}
        x={NumUtils.metersToMmRounded(dimensions.centerX)}
        onXChange={onXChange}
        y={NumUtils.metersToMmRounded(dimensions.centerY)}
        onYChange={onYChange}
        length={NumUtils.metersToMmRounded(dimensions.length)}
        onLengthChange={onLengthChange}
        width={NumUtils.metersToMmRounded(dimensions.width)}
        onWidthChange={onWidthChange}
        angle={dimensions.angle}
        onAngleChange={onAngleChange}
      />
    </Accordion>
  );
}

export const HighwayDimensions = memo(HighwayDimensionsComponent);
